"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { parseNumberInput } from "@/lib/parse-number";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const LETTER_THRESHOLDS: { min: number; letter: string }[] = [
  { min: 93, letter: "A" },
  { min: 90, letter: "A-" },
  { min: 87, letter: "B+" },
  { min: 83, letter: "B" },
  { min: 80, letter: "B-" },
  { min: 77, letter: "C+" },
  { min: 73, letter: "C" },
  { min: 70, letter: "C-" },
  { min: 67, letter: "D+" },
  { min: 63, letter: "D" },
  { min: 60, letter: "D-" },
];

function letterForPercent(percent: number): string {
  for (const threshold of LETTER_THRESHOLDS) {
    if (percent >= threshold.min) {
      return threshold.letter;
    }
  }
  return "F";
}

interface TestRow {
  id: number;
  score: string;
  possible: string;
}

let nextRowId = 0;

function makeRow(score: string): TestRow {
  nextRowId += 1;
  return { id: nextRowId, score, possible: "100" };
}

interface AverageResult {
  average: number;
  count: number;
  percents: number[];
}

function computeAverage(rows: TestRow[]): AverageResult | null {
  const percents: number[] = [];
  for (const row of rows) {
    const score = parseNumberInput(row.score);
    const possible = parseNumberInput(row.possible);
    if (score !== null && possible !== null && possible > 0) {
      percents.push((score / possible) * 100);
    }
  }
  if (percents.length === 0) {
    return null;
  }
  const sum = percents.reduce((total, p) => total + p, 0);
  return { average: sum / percents.length, count: percents.length, percents };
}

function neededOnNextTest(average: number, count: number, target: number) {
  return target * (count + 1) - average * count;
}

/**
 * A percentage inside the working, written the reader's way.
 *
 * The rounding used to be spelled `(Math.round(n * 100) / 100).toString()`, and the
 * `toString` on the end emits a period in every language — so the working line read
 * `82.5% + 91%` under an average card that already said `86,75%`. The formatter is a
 * parameter because this is module level; the list it feeds is joined with ` + `, not a
 * comma, so a comma decimal stays unambiguous.
 */
function fmt(n: number, format: NumberFormatter): string {
  return format(n, { maximumFractionDigits: 2 });
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const TEST_AVERAGE_CALCULATOR_STRINGS = {
  testScores: "Test Scores",
  test: "Test",
  score: "Score",
  outOf: "Out of",
  remove: "Remove",
  removeAriaLabel: "Remove test {n}",
  addTest: "Add Test",
  average: "Average",
  letterGrade: "Letter grade",
  testsCounted: "Tests counted",
  nextTestHeading: "What Do I Need on the Next Test?",
  targetAverageLabel: "Target average %",
  targetSecured:
    "Your target average is already secured — even a 0% on the next test would keep you at or above it.",
  targetNeeded:
    "You need at least {needed}% on the next test to reach your target average.",
  targetUnreachable:
    "This target is not reachable with one more test — it would require a score above 100%. Try a lower target.",
  formulaPrefix: "needed = target × (n + 1) − average × n = ",
  enterTargetHint: "Enter a target average to see the required score.",
} as const;

function interpretNeeded(
  needed: number,
  strings: typeof TEST_AVERAGE_CALCULATOR_STRINGS,
  formatNum: NumberFormatter
): string {
  if (needed <= 0) {
    return strings.targetSecured;
  }
  if (needed <= 100) {
    return strings.targetNeeded.replace(
      "{needed}",
      formatNum(needed, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    );
  }
  return strings.targetUnreachable;
}

export default function TestAverageCalculatorWidget() {
  const strings = useToolUi(TEST_AVERAGE_CALCULATOR_STRINGS);
  const formatNum = useToolNumber();
  const [rows, setRows] = useState<TestRow[]>(() => [
    makeRow("88"),
    makeRow("92"),
    makeRow(""),
  ]);
  const [target, setTarget] = useState("90");

  const result = computeAverage(rows);
  const targetNum = parseNumberInput(target);
  const needed =
    result && targetNum !== null
      ? neededOnNextTest(result.average, result.count, targetNum)
      : null;

  function updateRow(id: number, patch: Partial<TestRow>) {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }

  function addRow() {
    setRows((prev) => [...prev, makeRow("")]);
  }

  function removeRow(id: number) {
    setRows((prev) => prev.filter((row) => row.id !== id));
  }

  return (
    <>
      {/* Score rows */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.testScores}
        </h2>
        <div className="space-y-3">
          {rows.map((row, index) => (
            <div
              className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-4"
              key={row.id}
            >
              <p className="w-16 font-medium text-muted-foreground text-sm">
                {strings.test} {index + 1}
              </p>
              <div className="space-y-1">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`test-score-${row.id}`}
                >
                  {strings.score}
                </label>
                <input
                  className="w-24 rounded-lg border border-border bg-background px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`test-score-${row.id}`}
                  min={0}
                  onChange={(e) => updateRow(row.id, { score: e.target.value })}
                  step="0.1"
                  type="number"
                  value={row.score}
                />
              </div>
              <div className="space-y-1">
                <label
                  className="block font-medium text-foreground text-xs"
                  htmlFor={`test-possible-${row.id}`}
                >
                  {strings.outOf}
                </label>
                <input
                  className="w-24 rounded-lg border border-border bg-background px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  id={`test-possible-${row.id}`}
                  min={0}
                  onChange={(e) =>
                    updateRow(row.id, { possible: e.target.value })
                  }
                  step="0.1"
                  type="number"
                  value={row.possible}
                />
              </div>
              <button
                aria-label={strings.removeAriaLabel.replace(
                  "{n}",
                  String(index + 1)
                )}
                className="rounded-lg border border-border bg-card px-3 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
                disabled={rows.length <= 1}
                onClick={() => removeRow(row.id)}
                type="button"
              >
                {strings.remove}
              </button>
            </div>
          ))}
        </div>
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
          onClick={addRow}
          type="button"
        >
          {strings.addTest}
        </button>
      </section>

      {/* Result */}
      {result && (
        <section className="space-y-3">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard
              label={strings.average}
              value={`${formatNum(result.average, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
            />
            <StatCard
              label={strings.letterGrade}
              value={letterForPercent(result.average)}
            />
            <StatCard label={strings.testsCounted} value={result.count} />
          </div>
          <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm leading-relaxed">
            ({result.percents.map((p) => `${fmt(p, formatNum)}%`).join(" + ")})
            ÷ {result.count} ={" "}
            {formatNum(result.average, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            %
          </p>
        </section>
      )}

      {/* Next-test solver */}
      {result && (
        <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.nextTestHeading}
          </h2>
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="target-average"
            >
              {strings.targetAverageLabel}
            </label>
            <input
              className="w-28 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="target-average"
              max={150}
              min={0}
              onChange={(e) => setTarget(e.target.value)}
              step="0.1"
              type="number"
              value={target}
            />
          </div>
          {needed !== null && targetNum !== null && (
            <div className="space-y-2">
              <p className="rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
                {interpretNeeded(needed, strings, formatNum)}
              </p>
              <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm leading-relaxed">
                {strings.formulaPrefix}
                {fmt(targetNum, formatNum)} × {result.count + 1} −{" "}
                {fmt(result.average, formatNum)} × {result.count} ={" "}
                {fmt(needed, formatNum)}%
              </p>
            </div>
          )}
          {needed === null && (
            <p className="text-muted-foreground text-sm">
              {strings.enterTargetHint}
            </p>
          )}
        </section>
      )}
    </>
  );
}
